import { Link } from 'react-router-dom';
import { BarChart3, Shield, Search, Zap } from 'lucide-react';

export default function Landing() {
  return (
    <div className="min-h-screen bg-surface-900 text-slate-200 font-sans selection:bg-brand-500/30">
      {/* Navbar */}
      <nav className="border-b border-surface-800 bg-surface-900/80 backdrop-blur fixed w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center gap-2">
              <img
                src="/logo-text-v2.svg"
                alt="EdFrame"
                className="h-8 w-auto"
              />
            </div>
            <Link
              to="/login"
              className="px-4 py-2 bg-surface-800 hover:bg-surface-700 text-white rounded-lg transition-all text-sm font-medium border border-surface-700"
            >
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Gradients */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl -z-10 pointer-events-none">
          <div className="absolute top-20 left-20 w-72 h-72 bg-brand-500/10 rounded-full blur-3xl mix-blend-screen" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl mix-blend-screen" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-surface-800/50 border border-surface-700 text-brand-400 text-xs font-medium mb-8">
            <span className="w-2 h-2 rounded-full bg-brand-400 animate-pulse" />
            Discover Your Digital Classroom
          </div>

          <h1 className="text-5xl sm:text-7xl font-bold text-white tracking-tight mb-8">
            See What Products Your <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-400 to-blue-400">
              Students Are Actually Using
            </span>
          </h1>

          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
            EdFrame provides privacy-first analytics for schools. Discover unapproved apps,
            optimize license usage, and ensure student safety without compromising privacy.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="px-8 py-4 bg-brand-600 hover:bg-brand-500 text-white rounded-xl font-semibold text-lg transition-all shadow-lg shadow-brand-500/25 flex items-center justify-center gap-2 group"
            >
              Get Started
              <Zap size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div id="features" className="py-24 bg-surface-800/30 border-y border-surface-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Feature 1 */}
            <div className="bg-surface-900 p-8 rounded-2xl border border-surface-800 hover:border-brand-500/30 transition-colors group">
              <div className="w-12 h-12 bg-surface-800 rounded-xl flex items-center justify-center mb-6 text-brand-400 group-hover:scale-110 transition-transform">
                <Search size={24} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Automated Discovery</h3>
              <p className="text-slate-400 leading-relaxed">
                Automatically identify every EdTech tool being used across your district.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-surface-900 p-8 rounded-2xl border border-surface-800 hover:border-brand-500/30 transition-colors group">
              <div className="w-12 h-12 bg-surface-800 rounded-xl flex items-center justify-center mb-6 text-blue-400 group-hover:scale-110 transition-transform">
                <Shield size={24} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Privacy First</h3>
              <p className="text-slate-400 leading-relaxed">
                Built with student privacy at its core. We only track FQDNs - no URL parameters, no search terms, no PII.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-surface-900 p-8 rounded-2xl border border-surface-800 hover:border-brand-500/30 transition-colors group">
              <div className="w-12 h-12 bg-surface-800 rounded-xl flex items-center justify-center mb-6 text-purple-400 group-hover:scale-110 transition-transform">
                <BarChart3 size={24} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Actionable Insights</h3>
              <p className="text-slate-400 leading-relaxed">
                Make data-driven purchasing decisions. See which paid licenses are going unused and which free tools are vital.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 border-t border-surface-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-500">
            &copy; {new Date().getFullYear()} EdFrame Analytics. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

